import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Modal,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import { toast } from "react-hot-toast";
import { PaginatedTable, applyColumnFilters } from "../../components";
import { useAuth } from "../../contexts/auth";
import { baseAxios, Context } from "../../utils/config";
import { colors } from "../../theme/colors";

const STEPS = [
  "Prepare",
  "Reorder Selection",
  "Proposed Order",
  "Actual Order Confirmation",
];

const CONTROL_RADIUS = "6px";

function rowKey(row) {
  return `${row.inventory_location_id}-${row.inventory_product_id}`;
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatQty(value) {
  const n = toNumber(value);
  return (Math.round(n * 100) / 100).toFixed(2);
}

function normalizeReorderItem(item) {
  return {
    ...item,
    inventory_current_quantity: toNumber(item.inventory_current_quantity),
    inventory_desired_quantity: toNumber(item.inventory_desired_quantity),
    inventory_quantity_needed: toNumber(item.inventory_quantity_needed),
    inventory_product_price: toNumber(item.inventory_product_price),
    inventory_last_adjustment_quantity:
      item.inventory_last_adjustment_quantity == null ||
      item.inventory_last_adjustment_quantity === ""
        ? null
        : toNumber(item.inventory_last_adjustment_quantity),
  };
}

function formatLastOrdered(value) {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid() || d.year() < 2000) return "—";
  return d.format("M/D/YY h:mm A");
}

function quantityToOrder(item) {
  let value =
    toNumber(item.inventory_desired_quantity) -
    toNumber(item.inventory_current_quantity);

  if (value - Math.floor(value) > 0.25) {
    value = Math.ceil(value);
  } else {
    value = Math.floor(value);
  }

  return Math.max(value, 1);
}

function qtyColor(value) {
  const n = toNumber(value);
  if (n < 0) return "#ed1a1a";
  if (n > 0) return "#03a300";
  return "inherit";
}

function textCell(value) {
  return (
    <Typography
      component="span"
      fontSize={12}
      sx={{ overflowWrap: "break-word", lineHeight: 1.3 }}
    >
      {value || "—"}
    </Typography>
  );
}

const DEFAULT_SORT_BY = "inventory_quantity_needed";
const DEFAULT_SORT_ORDER = "desc";

const SELECTION_FILTER_FIELDS = new Set([
  "manufacturer_name",
  "brand_name",
  "category_name",
  "product_name",
  "inventory_quantity_needed",
  "inventory_current_quantity",
  "inventory_desired_quantity",
  "inventory_last_adjustment_quantity",
  "inventory_last_changed_timestamp",
]);

function pickFilters(filters, fieldSet) {
  const next = {};
  Object.entries(filters || {}).forEach(([field, value]) => {
    if (fieldSet.has(field)) next[field] = value;
  });
  return next;
}

function rowMatchesSearch(row, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    row.manufacturer_name,
    row.brand_name,
    row.category_name,
    row.product_name,
    row.product_upc_code,
    row.inventory_notes,
    row.location_name,
    row.reseller_company_name,
    String(row.inventory_quantity_needed ?? ""),
    formatQty(row.inventory_quantity_needed),
    String(row.inventory_current_quantity ?? ""),
    formatQty(row.inventory_current_quantity),
    String(row.inventory_desired_quantity ?? ""),
    formatQty(row.inventory_desired_quantity),
    String(row.inventory_last_adjustment_quantity ?? ""),
    formatQty(row.inventory_last_adjustment_quantity),
    String(row.new_quantity ?? ""),
    formatLastOrdered(row.inventory_last_changed_timestamp),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (field === "inventory_last_changed_timestamp") {
    return dayjs(va).valueOf() - dayjs(vb).valueOf();
  }
  if (
    typeof va === "number" ||
    typeof vb === "number" ||
    [
      "inventory_quantity_needed",
      "inventory_current_quantity",
      "inventory_desired_quantity",
      "inventory_last_adjustment_quantity",
      "new_quantity",
    ].includes(field)
  ) {
    return toNumber(va) - toNumber(vb);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function sortRows(rows, field, order) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const c = compareRows(a, b, field);
    return order === "asc" ? c : -c;
  });
  return copy;
}

/**
 * Reorder Inventory — multi-step order wizard
 */
export default function Order() {
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);

  const location =
    globalLocation?.location_id && globalLocation.location_id !== -1
      ? {
          location_id: globalLocation.location_id,
          location_name: globalLocation.location_name || "",
          org_name: globalLocation.org_name || user.org || "",
        }
      : {
          location_id: user.location_id,
          location_name: user.location_name || "",
          org_name: user.org || "",
        };

  const [activeStep, setActiveStep] = useState(0);
  const [dataSource, setDataSource] = useState([]);
  const [resellerList, setResellerList] = useState([]);
  const [reseller, setReseller] = useState(null);
  const [selectedMap, setSelectedMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [search, setSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const resultTableRef = useRef();

  const selected = useMemo(() => Object.values(selectedMap), [selectedMap]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await baseAxios.get(`organization/resellers`);
        setResellerList(res.data.resellers || []);
      } catch {
        toast.error("Error loading distributors", { duration: 3000 });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generatePdfBlob = useCallback(async () => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(16);
      const distributorText = reseller?.reseller_company_name
        ? ` - Order for ${reseller.reseller_company_name} Distributor`
        : "";
      pdf.text(
        `${location?.org_name || ""} ${location?.location_name || ""}${distributorText}`,
        20,
        20
      );

      pdf.setFontSize(12);
      pdf.text(`Submitted on ${new Date().toLocaleDateString()}`, 20, 30);
      pdf.text(`Order Number: ${orderNumber}`, 20, 40);

      const startY = 50;
      const startX = 20;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const tableWidth = pageWidth - 40;
      const colWidths = [35, 35, 30, 50, 25];
      const rowHeight = 8;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      const headers = [
        "Location",
        "Manufacturer",
        "Brand",
        "Item",
        "Order Qty",
      ];
      let currentX = startX;
      let currentY = startY;

      pdf.rect(startX, currentY, tableWidth, rowHeight);
      headers.forEach((header, index) => {
        pdf.text(header, currentX + 2, currentY + 5);
        if (index < headers.length - 1) {
          pdf.line(
            currentX + colWidths[index],
            currentY,
            currentX + colWidths[index],
            currentY + rowHeight
          );
        }
        currentX += colWidths[index];
      });

      currentY += rowHeight;
      pdf.setFont("helvetica", "normal");

      sortRows(selected, sortBy, sortOrder).forEach((item) => {
        if (currentY + rowHeight > pdf.internal.pageSize.getHeight() - 20) {
          pdf.addPage();
          currentY = 20;
        }

        currentX = startX;
        pdf.rect(startX, currentY, tableWidth, rowHeight);

        const rowData = [
          item.location_name || "-",
          item.manufacturer_name || "-",
          item.brand_name || "-",
          item.product_name || "-",
          String(item.new_quantity || "-"),
        ];

        rowData.forEach((data, colIndex) => {
          let text = String(data);
          const maxWidth = colWidths[colIndex] - 4;
          while (pdf.getTextWidth(text) > maxWidth && text.length > 3) {
            text = `${text.substring(0, text.length - 4)}...`;
          }
          pdf.text(text, currentX + 2, currentY + 5);
          if (colIndex < rowData.length - 1) {
            pdf.line(
              currentX + colWidths[colIndex],
              currentY,
              currentX + colWidths[colIndex],
              currentY + rowHeight
            );
          }
          currentX += colWidths[colIndex];
        });

        currentY += rowHeight;
      });

      return pdf.output("blob");
    } catch (error) {
      toast.error(`Error generating PDF: ${error.message}`, { duration: 3000 });
      return null;
    }
  }, [location, orderNumber, reseller, selected, sortBy, sortOrder]);

  const submitReorder = useCallback(async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      setActiveStep(3);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const payload = new FormData();
      const pdfBlob = await generatePdfBlob();
      payload.append(
        "data",
        JSON.stringify({
          reseller_id: reseller?.reseller_id || null,
          order_items: selected,
          order_number: orderNumber,
        })
      );
      payload.append("orderPdf", pdfBlob, `order-${orderNumber}.pdf`);
      await baseAxios.post("/inventory/reorder", payload);
      toast.success("Reorder submitted", { duration: 2500 });
    } catch {
      toast.error("Error processing request", { duration: 3000 });
      setActiveStep(2);
    } finally {
      setLoading(false);
    }
  }, [generatePdfBlob, orderNumber, reseller, selected]);

  const handleBack = useCallback(() => {
    if (activeStep === 1) {
      setOrderNumber("");
      setSelectedMap({});
    }
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, [activeStep]);

  const handlePrint = useCallback(async () => {
    const pdfBlob = await generatePdfBlob();
    if (!pdfBlob) return;
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `order-${orderNumber}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  }, [generatePdfBlob, orderNumber]);

  const columnFilterConfig = useMemo(
    () => ({
      inventory_quantity_needed: { filterType: "number" },
      inventory_current_quantity: { filterType: "number" },
      inventory_desired_quantity: { filterType: "number" },
      inventory_last_adjustment_quantity: { filterType: "number" },
      inventory_last_changed_timestamp: {
        getFilterText: (row) =>
          formatLastOrdered(row.inventory_last_changed_timestamp),
      },
    }),
    []
  );

  const proposedFilterConfig = useMemo(
    () => ({
      ...columnFilterConfig,
      new_quantity: { filterType: "number" },
    }),
    [columnFilterConfig]
  );

  const selectionColumnFilters = useMemo(
    () => pickFilters(columnFilters, SELECTION_FILTER_FIELDS),
    [columnFilters]
  );

  const filteredRows = useMemo(
    () => dataSource.filter((row) => rowMatchesSearch(row, search)),
    [dataSource, search]
  );

  const sortedRows = useMemo(
    () => sortRows(filteredRows, sortBy, sortOrder),
    [filteredRows, sortBy, sortOrder]
  );

  const columnFilteredRows = useMemo(
    () =>
      applyColumnFilters(
        sortedRows,
        selectionColumnFilters,
        columnFilterConfig
      ),
    [sortedRows, selectionColumnFilters, columnFilterConfig]
  );

  const proposedRows = useMemo(() => {
    const matching = selected.filter((row) => rowMatchesSearch(row, search));
    return sortRows(matching, sortBy, sortOrder);
  }, [search, selected, sortBy, sortOrder]);

  const proposedVisibleRows = useMemo(
    () => applyColumnFilters(proposedRows, columnFilters, proposedFilterConfig),
    [proposedRows, columnFilters, proposedFilterConfig]
  );

  const visibleSelectedCount = useMemo(
    () =>
      columnFilteredRows.reduce(
        (count, row) => count + (selectedMap[rowKey(row)] ? 1 : 0),
        0
      ),
    [columnFilteredRows, selectedMap]
  );

  const confirmationRows = useMemo(
    () => sortRows(selected, sortBy, sortOrder),
    [selected, sortBy, sortOrder]
  );

  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      setLoading(true);
      try {
        const resellerId = reseller?.reseller_id || "null";
        const inventoryRes = await baseAxios.get(
          `inventory/${location.location_id}/${resellerId}/listPerLocationAndReseller`
        );
        setDataSource(
          (inventoryRes.data.products || []).map(normalizeReorderItem)
        );
        setSelectedMap({});
        setSearch("");
        setColumnFilters({});
        setSortBy(DEFAULT_SORT_BY);
        setSortOrder(DEFAULT_SORT_ORDER);
        setActiveStep(1);
      } catch {
        toast.error("Error loading inventory for reorder", { duration: 3000 });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (activeStep === 1) {
      if (!orderNumber.trim()) {
        toast.error("Please input an order number", { duration: 2500 });
        return;
      }
      const visibleSelected = columnFilteredRows.filter((row) =>
        Boolean(selectedMap[rowKey(row)])
      );
      if (visibleSelected.length === 0) {
        toast.error("Please select at least one item", { duration: 2500 });
        return;
      }
      const nextSelected = {};
      visibleSelected.forEach((item) => {
        nextSelected[rowKey(item)] = {
          ...item,
          new_quantity: quantityToOrder(item),
        };
      });
      setSelectedMap(nextSelected);
      setActiveStep(2);
      return;
    }

    if (activeStep === 2) {
      if (proposedVisibleRows.length === 0) {
        toast.error("Please select at least one item", { duration: 2500 });
        return;
      }
      if (proposedVisibleRows.length !== selected.length) {
        const nextSelected = {};
        proposedVisibleRows.forEach((item) => {
          nextSelected[rowKey(item)] = item;
        });
        setSelectedMap(nextSelected);
      }
      setConfirmOpen(true);
      return;
    }

    if (activeStep === 3) {
      setSelectedMap({});
      setOrderNumber("");
      setDataSource([]);
      setSearch("");
      setColumnFilters({});
      setSortBy(DEFAULT_SORT_BY);
      setSortOrder(DEFAULT_SORT_ORDER);
      setActiveStep(0);
    }
  }, [
    activeStep,
    columnFilteredRows,
    location.location_id,
    orderNumber,
    proposedVisibleRows,
    reseller,
    selected.length,
    selectedMap,
  ]);

  const allFilteredSelected =
    columnFilteredRows.length > 0 &&
    columnFilteredRows.every((row) => Boolean(selectedMap[rowKey(row)]));
  const someFilteredSelected =
    columnFilteredRows.some((row) => Boolean(selectedMap[rowKey(row)])) &&
    !allFilteredSelected;

  const toggleRow = useCallback((row) => {
    const key = rowKey(row);
    setSelectedMap((prev) => {
      if (prev[key]) {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: row };
    });
  }, []);

  const toggleAllFiltered = useCallback(() => {
    setSelectedMap((prev) => {
      if (allFilteredSelected) {
        const next = { ...prev };
        columnFilteredRows.forEach((row) => {
          delete next[rowKey(row)];
        });
        return next;
      }
      const next = {};
      columnFilteredRows.forEach((row) => {
        next[rowKey(row)] = row;
      });
      return next;
    });
  }, [allFilteredSelected, columnFilteredRows]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const handleColumnFilterChange = useCallback((field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const onItemChange = useCallback((key, value) => {
    setSelectedMap((prev) => {
      if (!prev[key]) return prev;
      return {
        ...prev,
        [key]: {
          ...prev[key],
          new_quantity: value === "" ? "" : toNumber(value),
        },
      };
    });
  }, []);

  const proposedColumns = useMemo(
    () => [
      {
        id: "location_name",
        label: "Location",
        field: "location_name",
        width: "11%",
        render: (v) => textCell(v),
      },
      {
        id: "reseller_company_name",
        label: "Reseller",
        field: "reseller_company_name",
        width: "11%",
        render: (v) => textCell(v),
      },
      {
        id: "manufacturer_name",
        label: "Manufacturer",
        field: "manufacturer_name",
        width: "11%",
        render: (v) => textCell(v),
      },
      {
        id: "brand_name",
        label: "Brand",
        field: "brand_name",
        width: "10%",
        render: (v) => textCell(v),
      },
      {
        id: "category_name",
        label: "Category",
        field: "category_name",
        width: "10%",
        render: (v) => textCell(v),
      },
      {
        id: "product_name",
        label: "Item",
        field: "product_name",
        width: "18%",
        render: (v) => textCell(v),
      },
      {
        id: "inventory_current_quantity",
        label: "Item Count",
        field: "inventory_current_quantity",
        filterType: "number",
        width: "9%",
        render: (v) => (
          <Typography
            component="span"
            fontSize={13}
            fontWeight={600}
            sx={{ color: qtyColor(v), lineHeight: 1.2 }}
          >
            {formatQty(v)}
          </Typography>
        ),
      },
      {
        id: "inventory_desired_quantity",
        label: "Max Count",
        field: "inventory_desired_quantity",
        filterType: "number",
        width: "9%",
        render: (v) => (
          <Typography component="span" fontSize={13} sx={{ lineHeight: 1.2 }}>
            {formatQty(v)}
          </Typography>
        ),
      },
      {
        id: "new_quantity",
        label: "Order Quantity",
        field: "new_quantity",
        filterType: "number",
        sortable: false,
        width: "11%",
        render: (_, row) => {
          const key = rowKey(row);
          return (
            <TextField
              size="small"
              type="number"
              value={row.new_quantity ?? ""}
              onChange={(e) => onItemChange(key, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              inputProps={{ min: 1, step: 1 }}
              sx={{
                width: "100%",
                maxWidth: 112,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  borderRadius: CONTROL_RADIUS,
                  fontWeight: 600,
                  fontSize: 13,
                  height: 28,
                  minWidth: 0,
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.25,
                  px: 0.75,
                  minWidth: 0,
                },
              }}
            />
          );
        },
      },
    ],
    [onItemChange]
  );

  const selectionColumns = useMemo(
    () => [
      {
        id: "select",
        label: (
          <Checkbox
            size="small"
            checked={allFilteredSelected}
            indeterminate={someFilteredSelected}
            onChange={toggleAllFiltered}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                color: colors.gold,
              },
              p: 0.25,
            }}
          />
        ),
        sortable: false,
        filterable: false,
        width: "4%",
        render: (_, row) => (
          <Checkbox
            size="small"
            checked={Boolean(selectedMap[rowKey(row)])}
            onChange={() => toggleRow(row)}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.7)"
                  : colors.black,
              "&.Mui-checked": { color: colors.gold },
              p: 0.25,
            }}
          />
        ),
      },
      {
        id: "manufacturer_name",
        label: "Manufacturer",
        field: "manufacturer_name",
        width: "12%",
        render: (v) => textCell(v),
      },
      {
        id: "brand_name",
        label: "Brand",
        field: "brand_name",
        width: "10%",
        render: (v) => textCell(v),
      },
      {
        id: "category_name",
        label: "Category",
        field: "category_name",
        width: "10%",
        render: (v) => textCell(v),
      },
      {
        id: "product_name",
        label: "Item",
        field: "product_name",
        width: "18%",
        render: (v) => textCell(v),
      },
      {
        id: "inventory_quantity_needed",
        label: "Order Quantity",
        field: "inventory_quantity_needed",
        filterType: "number",
        width: "9%",
        render: (v) => formatQty(v),
      },
      {
        id: "inventory_current_quantity",
        label: "Item Count",
        field: "inventory_current_quantity",
        filterType: "number",
        width: "9%",
        render: (v) => (
          <Typography
            component="span"
            fontSize={14}
            fontWeight={600}
            sx={{ color: qtyColor(v) }}
          >
            {formatQty(v)}
          </Typography>
        ),
      },
      {
        id: "inventory_desired_quantity",
        label: "Max Count",
        field: "inventory_desired_quantity",
        filterType: "number",
        width: "9%",
        render: (v) => formatQty(v),
      },
      {
        id: "inventory_last_adjustment_quantity",
        label: "Last Order Count",
        field: "inventory_last_adjustment_quantity",
        filterType: "number",
        width: "10%",
        render: (v) => (v == null ? "—" : formatQty(v)),
      },
      {
        id: "inventory_last_changed_timestamp",
        label: "Last Ordered",
        field: "inventory_last_changed_timestamp",
        getFilterText: (row) =>
          formatLastOrdered(row.inventory_last_changed_timestamp),
        width: "9%",
        render: (v) => textCell(formatLastOrdered(v)),
      },
    ],
    [
      allFilteredSelected,
      selectedMap,
      someFilteredSelected,
      toggleAllFiltered,
      toggleRow,
    ]
  );

  const nextLabel =
    activeStep === 0 || activeStep === 1
      ? "Next"
      : activeStep === 2
        ? "Reorder"
        : "Start Over";

  const selectedChipCount =
    activeStep === 1
      ? visibleSelectedCount
      : activeStep === 2
        ? proposedVisibleRows.length
        : selected.length;

  return (
    <Box sx={{ px: 8, py: 3, width: "100%", minWidth: 0, overflowX: "hidden" }}>
      <Modal open={loading}>
        <Backdrop open={loading} sx={{ color: "#fff", zIndex: 1300 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress sx={{ color: colors.gold }} />
            <Typography variant="h6">Loading…</Typography>
          </Box>
        </Backdrop>
      </Modal>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Reorder Inventory
            </Typography>
            {location.location_name ? (
              <Chip
                size="small"
                label={location.location_name}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: CONTROL_RADIUS,
                }}
              />
            ) : null}
            {selectedChipCount > 0 && activeStep >= 1 ? (
              <Chip
                size="small"
                label={`${selectedChipCount} selected`}
                sx={{
                  bgcolor: colors.gold,
                  color: colors.black,
                  fontWeight: 600,
                  borderRadius: CONTROL_RADIUS,
                }}
              />
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Select a distributor, choose items to reorder, confirm quantities,
            and submit the order.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
          sx={{
            minHeight: 40,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: CONTROL_RADIUS,
          }}
        >
          Back
        </Button>

        <Stepper
          activeStep={activeStep}
          sx={{
            flex: 1,
            minWidth: 280,
            mx: 1,
            "& .MuiStepIcon-root.Mui-active": { color: colors.gold },
            "& .MuiStepIcon-root.Mui-completed": {
              color: (theme) =>
                theme.palette.mode === "dark" ? "#c8c8c8" : colors.black,
            },
            "& .MuiStepLabel-label.Mui-active": {
              color: (theme) =>
                theme.palette.mode === "dark" ? colors.gold : colors.black,
              fontWeight: 700,
            },
            "& .MuiStepLabel-label.Mui-completed": {
              color: (theme) =>
                theme.palette.mode === "dark" ? "#e8e8e8" : undefined,
            },
          }}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 1 ? (
          <TextField
            size="small"
            placeholder="Order Number"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: CONTROL_RADIUS,
              },
            }}
          />
        ) : null}

        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={loading}
          sx={{
            minHeight: 40,
            px: 2.5,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: CONTROL_RADIUS,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {nextLabel}
        </Button>

        {activeStep === 3 ? (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PdfIcon />}
            onClick={handlePrint}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Export to PDF
          </Button>
        ) : null}
      </Box>

      {activeStep === 0 ? (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Autocomplete
            sx={{
              minWidth: 300,
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: CONTROL_RADIUS,
              },
            }}
            value={reseller}
            getOptionLabel={(option) => option.reseller_company_name || ""}
            onChange={(_event, newValue) => setReseller(newValue)}
            size="small"
            options={resellerList}
            renderInput={(params) => (
              <TextField {...params} label="Distributor" />
            )}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ alignSelf: "center" }}
          >
            Leave distributor blank to include all products for this location.
          </Typography>
        </Box>
      ) : null}

      {activeStep === 1 || activeStep === 2 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: 1.5,
          }}
        >
          <TextField
            size="small"
            placeholder="Search item, brand, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: CONTROL_RADIUS,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      ) : null}

      {activeStep === 1 ? (
        <PaginatedTable
          columns={selectionColumns}
          rows={sortedRows}
          totalRowCount={sortedRows.length}
          loading={loading}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy={sortBy}
          initialOrder={sortOrder}
          searchQuery={`${search}|${JSON.stringify(selectionColumnFilters)}|${dataSource.length}|${visibleSelectedCount}`}
          clientSidePagination
          showFilterRow
          columnFilters={selectionColumnFilters}
          columnFilterConfig={columnFilterConfig}
          onColumnFilterChange={handleColumnFilterChange}
          skeletonHeight="40px"
          getRowKey={(row) => rowKey(row)}
          fitWidth
          emptyMessage="No products found for this location / distributor"
        />
      ) : null}

      {activeStep === 2 ? (
        <PaginatedTable
          columns={proposedColumns}
          rows={proposedRows}
          totalRowCount={proposedRows.length}
          loading={false}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy={sortBy}
          initialOrder={sortOrder}
          searchQuery={`proposed|${search}|${JSON.stringify(columnFilters)}|${proposedRows.length}|${sortBy}|${sortOrder}`}
          clientSidePagination
          showFilterRow
          columnFilters={columnFilters}
          columnFilterConfig={proposedFilterConfig}
          onColumnFilterChange={handleColumnFilterChange}
          skeletonHeight="28px"
          getRowKey={(row) => rowKey(row)}
          fitWidth
          emptyMessage="No items in proposed order"
        />
      ) : null}

      {activeStep === 3 ? (
        <Box
          sx={{
            width: "fit-content",
            maxWidth: "100%",
            background: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            margin: "auto",
            py: 4,
            px: 1,
          }}
        >
          <div ref={resultTableRef}>
            <Box sx={{ background: "#fff", color: "#000", px: 4 }}>
              <Typography
                color="black"
                variant="h5"
                gutterBottom
                component="div"
                fontWeight={700}
              >
                {`${location?.org_name || ""} ${location?.location_name || ""}${
                  reseller?.reseller_company_name
                    ? ` - Order for ${reseller.reseller_company_name} Distributor`
                    : ""
                }`}
              </Typography>
              <Typography color="black" component="div">
                {`Submitted on ${new Date().toLocaleDateString()}`}
              </Typography>
              <Typography color="black" gutterBottom component="div">
                {`Order Number: ${orderNumber}`}
              </Typography>
              <TableContainer sx={{ mt: 2, boxShadow: "none" }}>
                <Table
                  size="small"
                  aria-label="order confirmation"
                  sx={{
                    "& td, & th": {
                      border: "1px solid black !important",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Location</TableCell>
                      <TableCell>Manufacturer</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Order Quantity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {confirmationRows.map((item) => (
                      <TableRow key={rowKey(item)}>
                        <TableCell>{item.location_name}</TableCell>
                        <TableCell>{item.manufacturer_name}</TableCell>
                        <TableCell>{item.brand_name || "—"}</TableCell>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>{item.new_quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </div>
        </Box>
      ) : null}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            bgcolor: colors.black,
            color: colors.white,
            fontWeight: 700,
          }}
        >
          Confirm reorder
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography>
            Reorder <strong>{selectedChipCount}</strong> product
            {selectedChipCount === 1 ? "" : "s"} for order{" "}
            <strong>{orderNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitReorder}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: CONTROL_RADIUS,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
